import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePublicationDetail } from '../../../hooks/usePublicationDetail';
import { useTenant } from '../../../contexts/TenantContext';
import './PublicationAnalytics.css';

interface PublicationAnalyticsProps {
  tenantId?: string;
}

const PublicationAnalytics: React.FC<PublicationAnalyticsProps> = ({ tenantId: propTenantId }) => {
  const { tenant } = useTenant();
  const tenantId = propTenantId || tenant?.id || '';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { publication, analytics, loading, loadAnalytics } = usePublicationDetail(id!, tenantId);

  useEffect(() => {
    if (id) {
      loadAnalytics();
    }
  }, [id, loadAnalytics]);

  if (loading) {
    return (
      <div className="publication-analytics">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (!publication) {
    return (
      <div className="publication-analytics">
        <div className="error-state">
          <p>⚠️ Publication introuvable</p>
          <button onClick={() => navigate(-1)} className="btn-back">
            ← Retour
          </button>
        </div>
      </div>
    );
  }

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      ARRIVAGE: '🍅',
      PROMOTION: '🎉',
      ANTI_GASPI: '🌱',
      EVENEMENT: '🎪',
      INFO: 'ℹ️',
    };
    return icons[type] || '📢';
  };

  return (
    <div className="publication-analytics">
      {/* Header */}
      <div className="analytics-header">
        <button onClick={() => navigate(-1)} className="btn-back">
          ← Retour
        </button>
        <div>
          <h1>📊 Statistiques</h1>
          <p className="publication-title-subtitle">
            {getTypeIcon(publication.type)} {publication.title}
          </p>
        </div>
      </div>

      {/* KPIs principaux */}
      <div className="analytics-kpis">
        <div className="kpi-large">
          <div className="kpi-icon">👁️</div>
          <div className="kpi-value">{analytics?.views_count || publication.views_count}</div>
          <div className="kpi-label">Vues totales</div>
        </div>

        <div className="kpi-large">
          <div className="kpi-icon">🛒</div>
          <div className="kpi-value">{analytics?.clicks_count || publication.clicks_count}</div>
          <div className="kpi-label">Clics sur produits</div>
        </div>

        <div className="kpi-large">
          <div className="kpi-icon">❤️</div>
          <div className="kpi-value">{publication.likes_count ?? 0}</div>
          <div className="kpi-label">J'aime</div>
        </div>

        <div className="kpi-large">
          <div className="kpi-icon">↗️</div>
          <div className="kpi-value">{analytics?.shares_count ?? publication.shares_count}</div>
          <div className="kpi-label">Partages</div>
        </div>

        <div className="kpi-large">
          <div className="kpi-icon">👥</div>
          <div className="kpi-value">{analytics?.unique_customers || 0}</div>
          <div className="kpi-label">Clients uniques</div>
        </div>

        <div className="kpi-large">
          <div className="kpi-icon">📈</div>
          <div className="kpi-value">{analytics?.engagement_rate.toFixed(1) || 0}%</div>
          <div className="kpi-label">Taux d'engagement</div>
        </div>
      </div>

      {/* Métriques secondaires */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-icon">⏱️</span>
            <span className="metric-title">Temps moyen de lecture</span>
          </div>
          <div className="metric-value">
            {analytics?.average_duration_seconds
              ? `${Math.floor(analytics.average_duration_seconds / 60)}m ${analytics.average_duration_seconds % 60}s`
              : '0s'}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-icon">📅</span>
            <span className="metric-title">Date de publication</span>
          </div>
          <div className="metric-value">
            {publication.published_at
              ? new Date(publication.published_at).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'Non publiée'}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-icon">📷</span>
            <span className="metric-title">Images</span>
          </div>
          <div className="metric-value">{publication.images?.length || 0}</div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-icon">🏷️</span>
            <span className="metric-title">Produits liés</span>
          </div>
          <div className="metric-value">{publication.products?.length || 0}</div>
        </div>
      </div>

      {/* Graphique de performance */}
      <div className="performance-section">
        <h2 className="section-title">Performance</h2>
        <div className="performance-bars">
          <div className="performance-bar-item">
            <div className="bar-label">
              <span>Vues</span>
              <span className="bar-value">{analytics?.views_count || publication.views_count}</span>
            </div>
            <div className="bar-container">
              <div
                className="bar-fill"
                style={{
                  width: `${Math.min(100, ((analytics?.views_count || publication.views_count) / Math.max(analytics?.views_count || publication.views_count, 100)) * 100)}%`,
                  background: '#3b82f6',
                }}
              ></div>
            </div>
          </div>

          <div className="performance-bar-item">
            <div className="bar-label">
              <span>Clics</span>
              <span className="bar-value">{analytics?.clicks_count || publication.clicks_count}</span>
            </div>
            <div className="bar-container">
              <div
                className="bar-fill"
                style={{
                  width: `${((analytics?.clicks_count || publication.clicks_count) / Math.max(analytics?.views_count || publication.views_count, 1)) * 100}%`,
                  background: '#10b981',
                }}
              ></div>
            </div>
          </div>

          <div className="performance-bar-item">
            <div className="bar-label">
              <span>Clients uniques</span>
              <span className="bar-value">{analytics?.unique_customers || 0}</span>
            </div>
            <div className="bar-container">
              <div
                className="bar-fill"
                style={{
                  width: `${((analytics?.unique_customers || 0) / Math.max(analytics?.views_count || publication.views_count, 1)) * 100}%`,
                  background: '#8b5cf6',
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Produits liés */}
      {publication.products && publication.products.length > 0 && (
        <div className="products-performance">
          <h2 className="section-title">Produits mis en avant</h2>
          <div className="products-table">
            <table>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Prix</th>
                  <th>Offre spéciale</th>
                </tr>
              </thead>
              <tbody>
                {publication.products.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="product-cell">
                        {item.product.image_url && (
                          <img src={item.product.image_url} alt={item.product.name} />
                        )}
                        <span>{item.product.name}</span>
                      </div>
                    </td>
                    <td>
                      {item.highlight_price && item.product.prices?.[0]
                        ? `${(item.product.prices[0].amount_cents / 100).toFixed(2)} €`
                        : '-'}
                    </td>
                    <td>
                      {item.special_offer && (
                        <span className="special-offer-badge">{item.special_offer}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Conseils d'amélioration */}
      <div className="tips-section">
        <h2 className="section-title">💡 Conseils pour améliorer vos résultats</h2>
        <div className="tips-grid">
          {(analytics?.engagement_rate || 0) < 20 && (
            <div className="tip-card">
              <div className="tip-icon">📸</div>
              <div className="tip-content">
                <h4>Ajoutez plus d'images</h4>
                <p>Les publications avec 2-3 images ont 40% d'engagement en plus</p>
              </div>
            </div>
          )}

          {(analytics?.average_duration_seconds || 0) < 30 && (
            <div className="tip-card">
              <div className="tip-icon">✍️</div>
              <div className="tip-content">
                <h4>Enrichissez votre contenu</h4>
                <p>Un contenu plus détaillé retient l'attention plus longtemps</p>
              </div>
            </div>
          )}

          {publication.products && publication.products.length === 0 && (
            <div className="tip-card">
              <div className="tip-icon">🛒</div>
              <div className="tip-content">
                <h4>Liez des produits</h4>
                <p>Facilitez l'achat en ajoutant des produits à votre publication</p>
              </div>
            </div>
          )}

          <div className="tip-card">
            <div className="tip-icon">⏰</div>
            <div className="tip-content">
              <h4>Publiez aux bonnes heures</h4>
              <p>8h, 12h et 18h sont les meilleurs moments</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicationAnalytics;
